import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Cookie, BarChart3, Megaphone, Shield, X } from 'lucide-react';
import { Link } from 'react-router-dom';

export default function CookieConsentModal({ isOpen, onClose, onSave }) {
  const { t } = useTranslation();
  const [preferences, setPreferences] = useState({
    necessary: true, // Always true, not toggleable
    analytics: false,
    marketing: false,
  });

  // Load saved preferences when modal opens
  useEffect(() => {
    if (isOpen) {
      const saved = {
        analytics: localStorage.getItem('analytics-consent') === 'true',
        marketing: localStorage.getItem('marketing-consent') === 'true',
      };
      setPreferences({ ...preferences, ...saved });
    }
  }, [isOpen]);

  const handleToggle = (category) => {
    setPreferences((prev) => ({
      ...prev,
      [category]: !prev[category],
    }));
  };

  const handleSave = () => {
    onSave(preferences);
    onClose();
  };

  const handleAcceptAll = () => {
    const allAccepted = {
      necessary: true,
      analytics: true,
      marketing: true,
    };
    setPreferences(allAccepted);
    onSave(allAccepted);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-2xl">
            <Cookie className="h-6 w-6 text-blue-600" />
            {t('cookieModal.title', 'Cookie-Einstellungen')}
          </DialogTitle>
          <DialogDescription className="text-base">
            {t('cookieModal.description', 'Wählen Sie, welche Cookies wir verwenden dürfen. Sie können Ihre Einstellungen jederzeit ändern.')}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Necessary Cookies */}
          <div className="border rounded-lg p-4 bg-gray-50">
            <div className="flex items-start justify-between">
              <div className="flex items-start gap-3 flex-1">
                <Shield className="h-5 w-5 text-green-600 mt-1 flex-shrink-0" />
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <h3 className="font-semibold text-gray-900">
                      {t('cookieModal.necessary.title', 'Notwendige Cookies')}
                    </h3>
                    <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full font-medium">
                      {t('cookieModal.alwaysActive', 'Immer aktiv')}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600">
                    {t('cookieModal.necessary.description', 'Diese Cookies sind für die Grundfunktionen der Website erforderlich. Dazu gehören Login-Session, Sicherheits-Token und grundlegende Funktionalitäten.')}
                  </p>
                </div>
              </div>
              <Checkbox checked={true} disabled className="mt-1" />
            </div>
          </div>

          {/* Analytics Cookies */}
          <div className="border rounded-lg p-4 hover:border-blue-300 transition-colors">
            <div className="flex items-start justify-between">
              <div className="flex items-start gap-3 flex-1">
                <BarChart3 className="h-5 w-5 text-blue-600 mt-1 flex-shrink-0" />
                <div className="flex-1">
                  <h3 className="font-semibold text-gray-900 mb-2">
                    {t('cookieModal.analytics.title', 'Analyse-Cookies')}
                  </h3>
                  <p className="text-sm text-gray-600 mb-2">
                    {t('cookieModal.analytics.description', 'Diese Cookies helfen uns zu verstehen, wie Sie unsere Website nutzen, damit wir sie verbessern können.')}
                  </p>
                  <div className="text-xs text-gray-500 space-y-1">
                    <div className="flex items-center gap-2">
                      <span className="font-medium">Service:</span>
                      <span>PostHog Analytics (EU-Server)</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{t('cookieModal.purpose', 'Zweck')}:</span>
                      <span>{t('cookieModal.analytics.purpose', 'Nutzerverhalten analysieren, UX verbessern')}</span>
                    </div>
                  </div>
                </div>
              </div>
              <Checkbox
                checked={preferences.analytics}
                onCheckedChange={() => handleToggle('analytics')}
                className="mt-1"
              />
            </div>
          </div>

          {/* Marketing Cookies */}
          <div className="border rounded-lg p-4 hover:border-orange-300 transition-colors">
            <div className="flex items-start justify-between">
              <div className="flex items-start gap-3 flex-1">
                <Megaphone className="h-5 w-5 text-orange-600 mt-1 flex-shrink-0" />
                <div className="flex-1">
                  <h3 className="font-semibold text-gray-900 mb-2">
                    {t('cookieModal.marketing.title', 'Marketing-Cookies')}
                  </h3>
                  <p className="text-sm text-gray-600 mb-2">
                    {t('cookieModal.marketing.description', 'Diese Cookies helfen uns, die Wirksamkeit unserer Werbekampagnen zu messen.')}
                  </p>
                  <div className="text-xs text-gray-500 space-y-1">
                    <div className="flex items-center gap-2">
                      <span className="font-medium">Service:</span>
                      <span>Google Ads Conversion Tracking</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{t('cookieModal.purpose', 'Zweck')}:</span>
                      <span>{t('cookieModal.marketing.purpose', 'Conversion-Messung, Werbe-Erfolg tracken')}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{t('cookieModal.dataProcessing', 'Datenverarbeitung')}:</span>
                      <span>USA (Google LLC)</span>
                    </div>
                  </div>
                  <div className="mt-2 text-xs bg-orange-50 border border-orange-200 rounded p-2 text-orange-800">
                    ⚠️ {t('cookieModal.marketing.warning', 'Hinweis: Daten werden in die USA übermittelt (EU-US Data Privacy Framework)')}
                  </div>
                </div>
              </div>
              <Checkbox
                checked={preferences.marketing}
                onCheckedChange={() => handleToggle('marketing')}
                className="mt-1"
              />
            </div>
          </div>
        </div>

        <div className="pt-4 border-t space-y-3">
          <p className="text-xs text-gray-500">
            {t('cookieModal.moreInfo', 'Weitere Informationen finden Sie in unserer')}{' '}
            <Link to="/privacy" className="text-blue-600 hover:underline font-medium">
              {t('cookieModal.privacyPolicy', 'Datenschutzerklärung')}
            </Link>
          </p>
          <div className="flex flex-col sm:flex-row gap-3">
            <Button onClick={handleSave} variant="outline" className="flex-1">
              {t('cookieModal.saveSelection', 'Auswahl speichern')}
            </Button>
            <Button onClick={handleAcceptAll} className="flex-1 bg-blue-600 hover:bg-blue-700">
              {t('cookieModal.acceptAll', 'Alle akzeptieren')}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
